/**
 * Feedback URL construction utilities.
 * Builds pre-filled SIM Classic URLs for both bug reports and feature requests,
 * with auto-captured metadata in the mrpibbmetadata custom field.
 */

/** SIM-C template ID for bug reports (AutoSIM rule handles CTI/routing). */
const BUG_TEMPLATE_ID = 'ee65202b-9afc-42aa-ab2e-e247826a5438';

/** SIM-C template ID for feature requests. */
const FEATURE_TEMPLATE_ID = '9ec7ee99-9eb6-472a-a37d-247d372af332';

/** SIM folder UUID for MrPibb feature requests. */
const FEATURE_FOLDER_ID = '90a2ede9-2783-4ab9-8f92-d76f69806e28';

/** Maximum characters for user input. Only the title is pre-filled (no description body in URL). */
const MAX_INPUT_LENGTH = 200;

export type FeedbackType = 'bug' | 'feature_request';

export interface FeedbackContext {
  userAlias: string;
  stage: string;
  conversationId?: string;
}

/**
 * Build the metadata value for the mrpibbmetadata custom field.
 * Contains investigation context on separate lines.
 */
export function buildMetadataValue(context: FeedbackContext): string {
  return [
    `userAlias=${context.userAlias}`,
    `stage=${context.stage}`,
    `conversationId=${context.conversationId ?? 'N/A'}`,
    `timestamp=${new Date().toISOString()}`,
  ].join('\n');
}

/**
 * Build the custom field URL params for mrpibbmetadata.
 * Uses bracket notation that SIM-C expects, without double-encoding brackets.
 */
function buildCustomFieldParams(metadata: string): string {
  return (
    `&customFields[full_text][0][id]=${encodeURIComponent('mrpibbmetadata')}` +
    `&customFields[full_text][0][contentType]=${encodeURIComponent('text/plain')}` +
    `&customFields[full_text][0][value]=${encodeURIComponent(metadata)}`
  );
}

/**
 * Build a pre-filled SIM Classic URL for bug reports.
 * Uses the bug template with metadata in the mrpibbmetadata custom field.
 * An AutoSIM rule handles CTI assignment and routing to the ticket queue.
 */
export function buildBugReportUrl(description: string, context: FeedbackContext): string {
  const truncatedDesc = description.slice(0, MAX_INPUT_LENGTH);
  const title = `[MrPibb Bug] ${truncatedDesc}`;
  const metadata = buildMetadataValue(context);

  const params = new URLSearchParams({
    template: BUG_TEMPLATE_ID,
    title,
  });

  return `https://issues.amazon.com/issues/create?${params.toString()}${buildCustomFieldParams(metadata)}`;
}

/**
 * Build a pre-filled SIM Classic URL for feature requests.
 * Uses the feature template with metadata in the mrpibbmetadata custom field.
 * The user fills in the rest of the template details in the SIM form.
 */
export function buildFeatureRequestUrl(description: string, context: FeedbackContext): string {
  const truncatedDesc = description.slice(0, MAX_INPUT_LENGTH);
  const title = `[MrPibb Feature] ${truncatedDesc}`;
  const metadata = buildMetadataValue(context);

  const params = new URLSearchParams({
    template: FEATURE_TEMPLATE_ID,
    assignedFolder: FEATURE_FOLDER_ID,
    title,
  });

  return `https://issues.amazon.com/issues/create?${params.toString()}${buildCustomFieldParams(metadata)}`;
}

/**
 * Build the appropriate feedback URL based on type.
 * Returns the URL to open in a new tab.
 */
export function buildFeedbackUrl(
  feedbackType: FeedbackType,
  description: string,
  context: FeedbackContext,
): string {
  if (feedbackType === 'bug') {
    return buildBugReportUrl(description, context);
  }
  return buildFeatureRequestUrl(description, context);
}

/**
 * Validate that a feedback description is non-empty and within limits.
 * Returns an error message string, or null if valid.
 */
export function validateDescription(description: string): string | null {
  const trimmed = description.trim();
  if (trimmed.length === 0) {
    return 'Please enter a description';
  }
  if (trimmed.length > MAX_INPUT_LENGTH) {
    return `Description must be ${MAX_INPUT_LENGTH} characters or less`;
  }
  return null;
}
